import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/**
 * Pivots one coverage payload into whichever grain is being read.
 *
 * The API returns a row per company, provider and utility type. Every view here is a fold of that
 * one payload rather than its own endpoint, so the totals can never disagree between tabs.
 */
export const GRAINS = {
  company: {
    label: 'Customer',
    key: r => r.companyId || 'unknown',
    name: r => r.companyName || 'Unknown company'
  },
  provider: {
    label: 'Provider',
    key: r => r.utilityProviderId || r.providerName || 'unknown',
    name: r => r.providerName || 'Unknown provider'
  },
  // Never undefined: the grid keys its rows on this, and an undefined id takes the whole tab down.
  utilityType: {
    label: 'Utility',
    key: r => r.utilityTypeName || 'unknown',
    name: r => r.utilityTypeName || 'Unknown utility'
  }
};

/** bill-automation could not say how bills arrived, so no automated count on the page is measured. */
export const isAutomatedUnknown = function () {
  let rows = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  return (Array.isArray(rows) ? rows : []).some(r => r.automatedUnknown);
};

/**
 * Manual is what is left after automation, not a column of its own: a utility is either proven
 * end to end or somebody is still fetching it. Wired sits between them and is a subset of manual,
 * so adding the three would double count.
 */
export const pivotCoverage = function () {
  let rows = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  let grain = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'company';
  const spec = GRAINS[grain] || GRAINS.company;
  const buckets = new Map();
  for (const row of Array.isArray(rows) ? rows : []) {
    const key = spec.key(row);
    const bucket = buckets.get(key) || {
      key,
      name: spec.name(row),
      utilities: 0,
      automated: 0,
      wired: 0,
      notProven: 0
    };
    const wired = row.wiredUtilities || 0;
    const automated = row.automatedUtilities || 0;
    bucket.utilities += row.utilities || 0;
    bucket.automated += automated;
    bucket.wired += wired;
    // Clamped per source row, not on the fold. Automation is credited even where the registry has
    // no current fetcher, so a row at 0 wired / 10 automated would otherwise cancel out a sibling
    // row at 10 wired / 0 automated and report nothing left to prove.
    bucket.notProven += Math.max(0, wired - automated);
    buckets.set(key, bucket);
  }

  // One failed lookup leaves every row's automated count unknown: blank, not a measured 0.
  const unknown = isAutomatedUnknown(rows);
  return [...buckets.values()].map(b => unknown ? _objectSpread(_objectSpread({}, b), {}, {
    automated: null,
    notProven: null
  }) : b)
  // percent is materialised, not computed in a renderCell: the grid sorts and filters on the
  // row value, so a column that only exists at render time orders by undefined.
  .map(b => _objectSpread(_objectSpread({}, b), {}, {
    manual: b.automated == null ? null : Math.max(0, b.utilities - b.automated),
    percent: b.automated == null ? null : b.utilities ? Math.round(b.automated / b.utilities * 100) : 0
  }))
  // Most manual first: the top row is always the next thing worth automating.
  .sort((a, b) => (b.manual || 0) - (a.manual || 0) || b.utilities - a.utilities);
};

/** Share of utilities proven automated end to end. Weighted by utilities, never by row count. */
export const automatedShare = function () {
  let rows = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  const totals = (Array.isArray(rows) ? rows : []).reduce((acc, r) => ({
    utilities: acc.utilities + (r.utilities || 0),
    automated: acc.automated + (r.automatedUtilities || 0)
  }), {
    utilities: 0,
    automated: 0
  });
  if (isAutomatedUnknown(rows)) return {
    utilities: totals.utilities,
    automated: null,
    percent: null
  };
  return _objectSpread(_objectSpread({}, totals), {}, {
    percent: totals.utilities ? Math.round(totals.automated / totals.utilities * 100) : 0
  });
};